import apiClient from 'apiClient/index';
import classNames from 'classnames';
import { getLocationsString } from 'helpers/getLocationsString';
import useDebounce from 'hooks/useDebounce';
import React, {
  KeyboardEvent, useCallback, useEffect, useRef, useState,
} from 'react';
import { useEffectOnce } from 'react-use';
import { PetLocation } from 'stores/types/Profiles';
import { v4 as uuidv4 } from 'uuid';

type TProps = {
  onChange?: (location: PetLocation) => void,
  value?: PetLocation,
  options?: string,
  placeholder?: string,
  id?:string
  className?: string;
};

const DEFAULT_OPTIONS: string = 'types=place';

const LocationInput = ({
  value,
  onChange,
  options = DEFAULT_OPTIONS,
  placeholder = '',
  id,
  className,
}: TProps) => {
  const { debounce } = useDebounce();
  const inputElem = useRef<HTMLInputElement>(null);
  const [location, setLocation] = useState<PetLocation>();
  const [suggestions, setSuggestions] = useState<any>([]);
  const [sessionToken, setSessionToken] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);

  useEffectOnce(() => {
    if (!sessionToken) {
      setSessionToken(uuidv4());
    }
  });

  useEffect(() => {
    if (value && (
      value.locationId !== location?.locationId
      || value.country !== location?.country
    )) {
      setLocation(value);
      if (inputElem && inputElem.current) {
        inputElem.current.value = getLocationsString(value);
      }
    }
  }, [location?.country, location?.locationId, value]);

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.code) {
      case 'ArrowUp':
        e.preventDefault();
        if (activeIdx > 0) {
          setActiveIdx(activeIdx - 1);
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (activeIdx < suggestions.length - 1) {
          setActiveIdx(activeIdx + 1);
        } else if (activeIdx >= suggestions.length) {
          setActiveIdx(0);
        }
        break;
      case 'Enter':
      case 'Tab':
        if (suggestions && suggestions.length > 0) {
          e.preventDefault();
          if (activeIdx < suggestions.length) {
            selectSuggestion(suggestions[activeIdx]);
          }
        }
        break;
      default:
        break;
    }
  };

  const getSuggestions = useCallback((val?: string) => {
    if (val && val.length >= 2 && sessionToken) {
      const request = apiClient.mapbox.getLocationSuggestions(val, sessionToken, options);
      request.then(({ data }) => {
        if (data) {
          setSuggestions(data.suggestions);
        }
      });
    }
  }, [options, sessionToken]);

  const onTextChange = useCallback(
    debounce(getSuggestions, 500),
    [debounce, getSuggestions],
  );

  const getLocalityName = (suggestion: any) => `${suggestion.name}${suggestion.place_formatted ? `, ${suggestion.place_formatted}` : ''}`.trim();

  const selectSuggestion = (suggestion: any) => {
    const fullLocalityName = getLocalityName(suggestion);
    const locality = fullLocalityName
      ? fullLocalityName.split(',')[0]
      : '';
    const petLoc: PetLocation = {
      locationId: suggestion.mapbox_id,
      locality,
      region: suggestion.context?.region?.name,
      country: suggestion.context?.country?.country_code_alpha_3,
    };
    setLocation(petLoc);
    if (inputElem.current) {
      inputElem.current.value = getLocationsString(petLoc);
    }
    setSuggestions([]);
    setActiveIdx(0);
    if (onChange) {
      onChange(petLoc);
    }
  };

  return (
    <>
      <input
        id={id}
        ref={inputElem}
        className={classNames('form-control', className)}
        placeholder={placeholder}
        type="text"
        onChange={() => onTextChange(inputElem?.current?.value)}
        onKeyDown={onKeyDown}
      />
      <div
        className={classNames(
          suggestions && suggestions.length > 0 && 'show',
          'dropdown-menu',
        )}
        role="listbox"
        data-popper-placement="bottom-start"
      >
        {suggestions.map((sug: any, i: number) => (
          <button
            key={sug.mapbox_id}
            className={classNames(i === activeIdx && 'active', 'dropdown-item')}
            aria-selected={i === activeIdx}
            role="option"
            type="button"
            onMouseEnter={() => setActiveIdx(i)}
            onClick={() => selectSuggestion(sug)}
          >
            {getLocationsString({
              locality: getLocalityName(sug),
              region: sug.context?.region?.name,
              country: sug.context?.country?.country_code_alpha_3,
            } as PetLocation)}
          </button>
        ))}
      </div>
    </>
  );
};

export default LocationInput;
